import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDevice } from "../context/DeviceContext";
import { LoadingDisplay } from "./utils";
import { useParams } from "react-router-dom";
import { RefreshIcon } from "./icons";
import { useAuthStore } from "../store/authStore";
import { adminApi } from "../api/adminApi";
import { ownerApi } from "../api/ownerApi";
export default function HotspotLogsPage() {
    const { user } = useAuthStore();
const api = user?.role === "admin" ? adminApi : ownerApi;
    const { mikrotikId } = useParams();

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clearing, setClearing] = useState(false);

    // ---- Filters ----
    const [searchVoucher, setSearchVoucher] = useState("");
    const [searchVendo, setSearchVendo] = useState("");
    const [minAmount, setMinAmount] = useState("");
    const [maxAmount, setMaxAmount] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // ---- Sorting ----
    const [sortField, setSortField] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");

    // ---- Pagination ----
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    // Fetch logs
    const fetchLogs = useCallback(async () => {
        setLoading(true);

        try {
            const logsData = await api.getHotspotLogs(mikrotikId);
            setLogs(Array.isArray(logsData) ? logsData : []);
        } catch (error) {
            console.error("Failed to fetch hotspot logs:", error);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [api, mikrotikId]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Clear logs
    const handleClearLogs = async () => {
        if (!window.confirm("Clear ALL hotspot logs for this device?")) return;

        setClearing(true);
        try {
            await api.clearHotspotLogs(mikrotikId);
            await fetchLogs();
            alert("Logs cleared.");
        } catch {
            alert("Failed to clear logs.");
        } finally {
            setClearing(false);
        }
    };

    // Filter + Sort + Pagination
    const filteredLogs = useMemo(() => {
        return logs
            .filter(log =>
                log.voucher?.toLowerCase().includes(searchVoucher.toLowerCase()) &&
                log.vendoName?.toLowerCase().includes(searchVendo.toLowerCase()) &&
                (minAmount === "" || log.amount >= parseFloat(minAmount)) &&
                (maxAmount === "" || log.amount <= parseFloat(maxAmount)) &&
                (dateFrom === "" || new Date(log.createdAt) >= new Date(dateFrom)) &&
                (dateTo === "" || new Date(log.createdAt) <= new Date(dateTo + " 23:59:59"))
            )
            .sort((a, b) => {
                const valA = a[sortField];
                const valB = b[sortField];

                if (sortOrder === "asc") return valA > valB ? 1 : -1;
                return valA < valB ? 1 : -1;
            });
    }, [logs, searchVoucher, searchVendo, minAmount, maxAmount, dateFrom, dateTo, sortField, sortOrder]);

    const paginatedLogs = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredLogs.slice(start, start + rowsPerPage);
    }, [filteredLogs, page]);

    const totalPages = Math.ceil(filteredLogs.length / rowsPerPage);

    // Export CSV
    const exportCSV = () => {
        const header = [
            "Time,Voucher,Amount,Vendo,MAC,IP"
        ].join("\n");

        const rows = filteredLogs
            .map(log =>
                [
                    new Date(log.createdAt).toLocaleString(),
                    log.voucher,
                    log.amount,
                    log.vendoName,
                    log.macAddress,
                    log.clientIp
                ].join(",")
            )
            .join("\n");

        const csv = header + "\n" + rows;

        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `HotspotLogs-${mikrotikId}.csv`;
        a.click();

        URL.revokeObjectURL(url);
    };

    if (loading) return <LoadingDisplay message="Loading hotspot logs..." />;

    return (
        <div className="space-y-4">

            {/* --- HEADER --- */}
            <div className="flex justify-between items-center">
                <h1 className="text-xl font-bold text-gray-800">Hotspot Coin Logs</h1>

                <div className="flex gap-3">
                    <button
                        onClick={fetchLogs}
                        className="flex items-center gap-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm"
                    >
                        <RefreshIcon className="w-4 h-4" /> Refresh
                    </button>

                    <button
                        onClick={exportCSV}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                    >
                        Export CSV
                    </button>

                    <button
                        onClick={handleClearLogs}
                        disabled={clearing}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm"
                    >
                        {clearing ? "Clearing..." : "Clear Logs"}
                    </button>
                </div>
            </div>

            {/* --- FILTERS --- */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4 bg-white rounded shadow">

                <input className="input" placeholder="Filter Voucher"
                    value={searchVoucher} onChange={e => setSearchVoucher(e.target.value)} />

                <input className="input" placeholder="Filter Vendo"
                    value={searchVendo} onChange={e => setSearchVendo(e.target.value)} />

                <input type="number" className="input" placeholder="Min Amount"
                    value={minAmount} onChange={e => setMinAmount(e.target.value)} />

                <input type="number" className="input" placeholder="Max Amount"
                    value={maxAmount} onChange={e => setMaxAmount(e.target.value)} />

                <input type="date" className="input"
                    value={dateFrom} onChange={e => setDateFrom(e.target.value)} />

                <input type="date" className="input"
                    value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>

            {/* --- TABLE --- */}
            <div className="bg-white p-4 rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            {[
                                ["createdAt", "Time"],
                                ["voucher", "Voucher"],
                                ["amount", "Amount"],
                                ["vendoName", "Vendo"],
                                ["macAddress", "MAC"],
                                ["clientIp", "IP"]
                            ].map(([field, label]) => (
                                <th key={field}
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer"
                                    onClick={() => {
                                        setSortField(field);
                                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                                    }}
                                >
                                    {label}
                                    {sortField === field ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                        {paginatedLogs.map(log => (
                            <tr key={log.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">{new Date(log.createdAt).toLocaleString()}</td>
                                <td className="px-4 py-3 font-mono">{log.voucher}</td>
                                <td className="px-4 py-3">₱{log.amount?.toFixed(2)}</td>
                                <td className="px-4 py-3">{log.vendoName}</td>
                                <td className="px-4 py-3 font-mono text-xs">{log.macAddress}</td>
                                <td className="px-4 py-3 font-mono text-xs">{log.clientIp}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- PAGINATION --- */}
            <div className="flex justify-center items-center gap-3 mt-3">
                <button disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Prev</button>

                <span className="text-sm">Page {page} of {totalPages}</span>

                <button disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
            </div>
        </div>
    );
}
